<template>
  <section class="SubscriptionPromotions">
    <discount-box
      v-if="acceptPromotion"
      :title="$t('subscription_details.accordion.activateDiscount.discountBox.title', 'Insert voucher discount code')"
      :button-text="$t('subscription_details.accordion.activateDiscount.discountBox.button', 'Activate')"
      @apply-promotion-code="applyPromotionCodeTask.run($event)"
      :is-applying="applyPromotionCodeTask.isActive"
      :applied-result="appliedResult"
    />

    <div class="PriceCalculation">
      <h5 class="title">
        {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.title', 'Price Calculation') }}
      </h5>

      <div class="PriceCalculation_body">
        <!-- reloading charge mask -->
        <app-loading
          class="translucence"
          v-if="isReloading"
          component="looping-rhombuses-spinner"
          :animation-duration="2000"
          :size="30"
        />
        <!-- monthly charge -->
        <div class="PriceCalculation_formula" v-if="subscriptionTotalCharge.monthly.original">
          <div class="priceItem-wrapper">
            <p class="priceItem flex-between">
              <span class="name">
                {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.monthly', 'Monthly fee') }}
              </span>
              <span class="amount">
                {{ subscriptionTotalCharge.monthly.original | formatCurrency }}
              </span>
            </p>
            <p class="priceItem priceItem-discount flex-between" v-for="discount in subscriptionTotalCharge.monthly.discounts" :key="discount.id">
              <span class="name">
                {{ discount.description }}
              </span>
              <span class="amount">
                - {{ discount.amount | formatCurrency }}
              </span>
            </p>
          </div>

          <p class="price-final flex-between" v-if="subscriptionTotalCharge.monthly.discounted !== subscriptionTotalCharge.monthly.original">
            <span class="name">
              {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.AmountToPay', 'Amount to pay') }}
            </span>
            <span class="amount">
              {{ subscriptionTotalCharge.monthly.discounted | formatCurrency(true) }}
            </span>
          </p>
        </div>

        <!-- quarterly charge -->
        <div class="PriceCalculation_formula" v-if="subscriptionTotalCharge.quarterly.original">
          <div class="priceItem-wrapper">
            <p class="priceItem flex-between">
              <span class="name">
                {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.quarterly', 'Quarterly fee') }}
              </span>
              <span class="amount">
                {{ subscriptionTotalCharge.quarterly.original | formatCurrency }}
              </span>
            </p>
            <p class="priceItem priceItem-discount flex-between" v-for="discount in subscriptionTotalCharge.quarterly.discounts" :key="discount.id">
              <span class="name">
                {{ discount.description }}
              </span>
              <span class="amount">
                - {{ discount.amount | formatCurrency }}
              </span>
            </p>
          </div>

          <p class="price-final flex-between" v-if="subscriptionTotalCharge.quarterly.discounted !== subscriptionTotalCharge.quarterly.original">
            <span class="name">
              {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.AmountToPay', 'Amount to pay') }}
            </span>
            <span class="amount">
              {{ subscriptionTotalCharge.quarterly.discounted | formatCurrency(true) }}
            </span>
          </p>
        </div>

        <!-- semiAnnual charge -->
        <div class="PriceCalculation_formula" v-if="subscriptionTotalCharge.semiAnnual.original">
          <div class="priceItem-wrapper">
            <p class="priceItem flex-between">
              <span class="name">
                {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.semiAnnual', 'Semi Annual fee') }}
              </span>
              <span class="amount">
                {{ subscriptionTotalCharge.semiAnnual.original | formatCurrency }}
              </span>
            </p>
            <p class="priceItem priceItem-discount flex-between" v-for="discount in subscriptionTotalCharge.semiAnnual.discounts" :key="discount.id">
              <span class="name">
                {{ discount.description }}
              </span>
              <span class="amount">
                - {{ discount.amount | formatCurrency }}
              </span>
            </p>
          </div>

          <p class="price-final flex-between" v-if="subscriptionTotalCharge.semiAnnual.discounted !== subscriptionTotalCharge.semiAnnual.original">
            <span class="name">
              {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.AmountToPay', 'Amount to pay') }}
            </span>
            <span class="amount">
              {{ subscriptionTotalCharge.semiAnnual.discounted | formatCurrency(true) }}
            </span>
          </p>
        </div>

        <!-- specificMonths charge -->
        <div class="PriceCalculation_formula" v-show="show" v-for="(specificCharge, index) in subscriptionTotalCharge.specificMonths" :key="index">
          <div class="priceItem-wrapper">
            <p class="priceItem flex-between">
              <span class="name">
                {{
                  $t('subscription_details.accordion.activateDiscount.priceCalculation.specificMonths', {
                    defaultValue: 'x months fee',
                    amount: specificCharge.months
                  })
                }}
              </span>
              <span class="amount">
                {{ specificCharge.amount.original | formatCurrency }}
              </span>
            </p>
            <p class="priceItem priceItem-discount flex-between" v-for="discount in specificCharge.amount.discounts" :key="discount.id">
              <span class="name">
                {{ discount.description }}
              </span>
              <span class="amount">
                - {{ discount.amount | formatCurrency }}
              </span>
            </p>
          </div>
          <p class="price-final flex-between" v-if="specificCharge.amount.discounted !== specificCharge.amount.original">
            <span class="name">
              {{ $t('subscription_details.accordion.activateDiscount.priceCalculation.AmountToPay', 'Amount to pay') }}
            </span>
            <span class="amount">
              {{ specificCharge.amount.discounted | formatCurrency(true) }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { extractErrorMessage } from '@/utilities/common'
  import DiscountBox from 'components/DiscountBox'
  import Vue from 'vue'
  import { mapState } from 'vuex'

  export default {
    name: 'subscription-promotions',

    components: {
      DiscountBox
    },

    data () {
      return {
        appliedResult: {},
        isReloading: false,
        show: false
      }
    },

    computed: {
      ...mapState('subscription', {
        subscriptionNumber: state => state.subscriptionDetails.number,
        acceptPromotion: state => state.subscriptionDetails.acceptPromotion,
        subscriptionTotalCharge: state => state.subscriptionDetails.totalCharge,
        discountTermsAndConditions: state => state.subscriptionDetails.discountTermsAndConditions,
        hasDiscount: state => state.subscriptionDetails.hasDiscount
      })
    },

    tasks (t) {
      return {
        applyPromotionCodeTask: t(async function (promotionCode) {
          this.appliedResult = {}
          try {
            const { code, description } = await Vue.api.subscription.applyPromotionCode(this.subscriptionNumber, promotionCode)
            this.appliedResult = {
              type: 'success',
              message: this.$t('subscription_details.accordion.activateDiscount.discountBox.activate.success', 'Voucher code activated1'),
              promotion: {
                code,
                description
              }
            }
            this.isReloading = true
            this.$store.dispatch('subscription/fetchSubscriptionDetails', this.subscriptionNumber).catch(() => {
              this.$notify({
                type: 'error',
                title: this.$t('subscription_details.accordion.activateDiscount.priceCalculation.reload.failed', 'Failed to reload the new price, please reload the page manually')
              })
            }).finally(() => {
              this.isReloading = false
            })
          } catch (error) {
            this.appliedResult = {
              type: 'error',
              message: extractErrorMessage(error),
              promotion: null
            }
          }
        }).flow('restart')
      }
    }
  }
</script>

<style lang="scss">
  .SubscriptionPromotions {
    .DiscountBox {
      margin-bottom: .285rem;

      &_title {
        padding: 0 .13rem;
      }

      .coupon-apply-info {
        font-weight: 500;
        padding: 0 .13rem;
      }

      &_form {
        .input-discount {
          .Form_control {
            border: .02rem solid $blue;
            padding: .04rem .1rem;
          }
        }
      }
    }
  }

  .PriceCalculation {
    position: relative;

    .title {
      font-size: .17rem;
      font-weight: 800;
      line-height: .22rem;
      color: $blue;
      padding: 0 .13rem .09rem;
    }

    &_body {
      position: relative;
    }

    &_formula {
      border: .02rem solid $blue;

      + .PriceCalculation_formula {
        margin-top: .1rem;
      }

      .priceItem {
        &-wrapper {
          padding: .17rem .13rem;
        }

        font-size: .14rem;
        font-weight: 500;
        line-height: .18rem;

        &-discount {
          color: $blue;
        }

        + .priceItem {
          margin-top: .1rem;
        }

        .name {
          margin-right: .1rem;
        }

        .amount {
          flex-shrink: 0;
        }
      }

      .price-final {
        background: $green;
        color: #fff;
        font-size: .14rem;
        font-weight: 500;
        line-height: .2rem;
        padding: .09rem .13rem;

        .amount {
          font-size: .16rem;
        }
      }
    }

    .terms-discount {
      font-size: .1rem;
      font-weight: 500;
      padding: .15rem .13rem 0;
      color: $font-gray;
    }
  }
</style>
